<template>
  <main role="main" class="auth-wrapper d-flex flex-column align-items-center justify-content-center py-5">
    <div v-if="!successMessage" class="auth-card overflow-hidden">
      <b-row no-gutters class="h-100">
        <!-- Left Banner Panel -->
        <b-col lg="6" class="auth-banner d-none d-lg-flex position-relative flex-column justify-content-between p-5">
          <div class="auth-illustration-container">
            <!-- <img src="/images/arrow2.png" alt="VNPT GoMeet" class="auth-illustration"> -->
          </div>
          
          <div class="z-index-10 mt-auto">
            <h2 class="auth-banner-title">{{ $t("registerAccount") }}</h2>
            <p class="auth-banner-subtitle">{{ $t("registerAccountSubtitle") }}</p>
          </div>

          <div class="auth-banner-footer z-index-10 mt-5">
            <p class="mb-0 text-white-50">
              {{ $t("alreadyHaveAccountQ") }} 
              <router-link to="/login" class="auth-link-alt">{{ $t("login") }}</router-link>
            </p>
          </div>
        </b-col>
       
        <!-- Right Form Panel -->
        <b-col lg="6" cols="12" class="p-4 p-md-5 d-flex flex-column justify-content-center bg-white">
          <div class="form-header mb-5">
            <h3 class="auth-title">{{ $t("enterAccountInformation") }}</h3>
            <div class="title-divider"></div>
          </div>

          <b-form class="premium-form" novalidate @keydown="formKeyDown" @submit.prevent="onSubmit">
            <b-form-group label-class="auth-label">
              <template v-slot:label>Email <span class="text-danger">*</span></template>
              <div class="auth-input-wrapper">
                <i class="fas fa-envelope input-icon"></i>
                <b-form-input 
                  type="email" 
                  name="email" 
                  required 
                  v-model="email"
                  class="auth-input"
                  placeholder="name@company.com"
                />
              </div>
            </b-form-group>

            <div class="auth-checkbox-area mb-4">
              <b-form-checkbox v-model="agreeTerms" name="agreeTerms" class="premium-checkbox">
                <span class="checkbox-text">
                  {{ $t("signUpIAgreeWith") }}
                  <a href="https://vnptai.io/vi/dieukhoansudung" target="_blank" class="terms-link">{{ $t("signUpTermOfUse") }}</a>,
                  <a href="https://vnptai.io/vi/baovedulieucanhan" target="_blank" class="terms-link">{{ $t("personalDataProtectionPolicy") }}</a>
                  {{ $t("and") }}
                  <a href="https://vnptai.io/vi/chinhsachbaomat" target="_blank" class="terms-link">{{ $t("signUpPrivacyPolicy") }}</a>
                  {{ $t("productVnptGoMeet") }}
                </span>
              </b-form-checkbox>
            </div>

            <p v-if="formErrorMsg" class="error-msg mb-4">
              <i class="fas fa-exclamation-circle mr-2"></i>
              {{ $t(formErrorMsg) }}
            </p>

            <b-button 
              type="submit" 
              variant="primary" 
              block 
              class="btn-premium-auth"
              :disabled="isSubmitting || !agreeTerms" 
              @click="beforeSubmit"
            >
              <span v-if="!isSubmitting">{{ $t("register") }}</span>
              <b-spinner v-else small variant="light"></b-spinner>
            </b-button>
          </b-form>

          <div class="d-lg-none text-center mt-5">
            <p class="text-muted small">
              {{ $t("alreadyHaveAccountQ") }} 
              <router-link to="/login" class="text-primary font-weight-bold">{{ $t("login") }}</router-link>
            </p>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Success State -->
    <div v-else class="auth-card-success text-center p-5">
      <div class="success-icon-wrapper mb-4">
        <div class="success-icon-bg">
          <i class="fas fa-check"></i>
        </div>
      </div>
      <h2 class="auth-title mb-3">{{ $t("verificationEmailSent") }}</h2>
      <p class="success-text mb-4">{{ successMessage }}</p>
      
      <div class="resend-area p-4 rounded-lg bg-light mb-5">
        <p class="mb-2 text-muted">{{ $t("notGetVerificationEmailYetC") }}</p>
        <b-link class="font-weight-bold" @click="resendEmail">{{ $t("resendVerificationEmail") }}</b-link>
      </div>

      <div class="d-flex flex-column flex-md-row justify-content-center gap-3">
        <b-button  md="auto" class="premium-mail-btn gmail-btn mb-2 mb-md-0 mr-md-3" href="https://mail.google.com/mail" target="_blank">
          <i class="fab fa-google mr-2"></i> {{ $t("openGmail") }}
        </b-button>
        <b-button  md="auto" class="premium-mail-btn outlook-btn" href="https://outlook.live.com/mail" target="_blank">
          <i class="fab fa-windows mr-2"></i> {{ $t("openOutlook") }}
        </b-button>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
  .auth-wrapper {
    min-height: calc(100vh - 80px);
    background-color: #f8fafc;
  }

  .auth-card {
    width: 1000px;
    max-width: 95vw;
    height: 620px;
    background: #ffffff;
    border-radius: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.04);
  }

  .auth-card-success {
    width: 600px;
    max-width: 95vw;
    background: #ffffff;
    border-radius: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
  }

  /* Left Panel Styles */
  .auth-banner {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    overflow: hidden;
    border-right: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* Removed dark pseudo-glow for light theme */

  .auth-illustration-container {
    padding: 20px;
    z-index: 2;
    background-image: url("~/public/images/arrow2.png");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    height: 200px;
    opacity: 0.8;
  }

  .auth-illustration {
    width: 100%;
    height: auto;
    filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
    animation: float 6s ease-in-out infinite;
  }

  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
  }

  .auth-banner-title {
    color: #0f172a;
    font-weight: 700;
    font-size: 32px;
    margin-bottom: 12px;
  }

  .auth-banner-subtitle {
    color: #475569;
    font-size: 16px;
    line-height: 1.6;
    max-width: 400px;
  }

  .auth-link-alt {
    color: #004f9e;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; color: #137ae1; }
  }

  .auth-banner-footer p {
    color: #64748b !important;
  }

  /* Form Styles */
  .auth-title {
    font-weight: 700;
    color: #0f172a;
    font-size: 28px;
    margin-bottom: 8px;
  }

  .title-divider {
    width: 50px;
    height: 4px;
    background: #004f9e;
    border-radius: 2px;
  }

  .auth-label {
    font-weight: 600;
    font-size: 14px;
    color: #64748b;
    margin-bottom: 8px;
  }

  .auth-input-wrapper {
    position: relative;
    .input-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
      z-index: 10;
    }
  }

  .auth-input {
    height: 54px;
    padding-left: 45px !important;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background-color: #f8fafc;
    font-size: 15px;
    transition: all 0.3s ease;

    &:focus {
      background-color: #ffffff;
      border-color: #004f9e;
      box-shadow: 0 0 0 4px rgba(0, 79, 158, 0.08);
    }
  }

  .terms-link {
    color: #004f9e;
    font-weight: 600;
    text-decoration: underline;
  }

  .btn-premium-auth {
    height: 54px;
    background: linear-gradient(135deg, #004f9e 0%, #137ae1 100%);
    border: none;
    border-radius: 14px;
    font-weight: 600;
    font-size: 16px;
    box-shadow: 0 10px 20px rgba(0, 79, 158, 0.15);
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 15px 30px rgba(0, 79, 158, 0.25);
      filter: brightness(1.05);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }

  .error-msg {
    color: #ef4444;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
  }

  /* Success State Styles */
  .success-icon-bg {
    width: 80px;
    height: 80px;
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    margin: 0 auto;
  }

  .success-text {
    color: #64748b;
    font-size: 16px;
    line-height: 1.6;
  }

  .premium-mail-btn {
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 600;
    transition: all 0.2s ease;
    min-width: 180px;
    flex: 1;
    max-width: 220px;
    
    &.gmail-btn {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      color: #334155;
      &:hover { background: #f8fafc; border-color: #cbd5e1; }
    }
    
    &.outlook-btn {
      background: #0078d4;
      border: none;
      color: #ffffff;
      &:hover { background: #006abc; filter: brightness(1.1); }
    }
  }

  .z-index-10 { z-index: 10; }

  @media (max-width: 991px) {
    .auth-card { height: auto; }
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as userService from "@/service/userService";
  import * as validationUtils from "@/service/validationUtils";

  @Component
  export default class SignUp extends BaseComponent {

    isLoading = true;
    email = "";
    agreeTerms = false;

    formErrorMsg = "";
    isSubmitting = false;
    successMessage = "";

    secretKey = "";
    isSecretKeyValid = false;

    async mounted() {
      this.secretKey = this.$route.query.secretKey as string;

      if (this.secretKey) {
        this.isSecretKeyValid = true;
        return;
      }
    }

    formKeyDown(event: KeyboardEvent) {
      return event.key !== 'Enter';
    }

    beforeSubmit() {
      this.email = this.email.trim();
    }

    async onSubmit() {
      this.formErrorMsg = null;

      if (!validationUtils.isEmail(this.email)) {
        this.formErrorMsg ="invalidEmail";
        return;
      }

      try {
        this.isSubmitting = true;
        const email = this.email;

        const verifyRet = await userService.verifyEmail(email, this.isSecretKeyValid, this.secretKey);
        this.successMessage = verifyRet?.message || this.$t("verificationEmailSentSubtitle", { email }).toString();

      } catch (e) {
        this.formErrorMsg = e.response?.data?.code || e.code;

      } finally {
        this.isSubmitting = false;
      }
    }

    async resendEmail() {
      try {
        this.showLoading();
        const email = this.email;
        await userService.verifyEmail(email, this.isSecretKeyValid, this.secretKey);
        this.toastSuccessDelay(this.$t("verificationEmailSent").toString());

      } catch (e) {
        this.formErrorMsg = e.response?.data?.code || e.code;
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }

  }
</script>
